import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') setIsMobileMenuOpen(false);
    };
    document.addEventListener('keydown', closeOnEscape);
    return () => document.removeEventListener('keydown', closeOnEscape);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { path: '/', label: 'ACCUEIL', color: '' },
    { path: '/conception', label: 'CONCEPTION', color: 'text-orange-500' },
    { path: '/creation', label: 'CRÉATION', color: 'text-red-500' },
    { path: '/entretien', label: 'ENTRETIEN', color: 'text-green-600' },
    { path: '/contact', label: 'CONTACT', color: '' }
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-lg py-2' : 'bg-white py-4'
      }`}
    >
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link data-testid="header-home-logo-link" to="/" onClick={() => setIsMobileMenuOpen(false)} className="flex items-center min-w-0">
            <img 
              src="https://customer-assets.emergentagent.com/job_perinature-clone/artifacts/naygcplw_grok-image-fad8cf36-7371-4ec2-9859-535c45150ca6.png" 
              alt="Logo Perinature" 
              data-testid="header-logo-image"
              className="h-20 w-auto max-w-full object-contain"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav data-testid="desktop-navigation" aria-label="Navigation principale" className="hidden lg:flex items-center gap-12">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`desktop-nav-${item.path.slice(1) || 'home'}-link`}
                aria-current={location.pathname === item.path ? 'page' : undefined}
                className={`font-semibold text-sm transition-all duration-200 hover:scale-110 ${
                  location.pathname === item.path
                    ? item.color || 'text-gray-900'
                    : 'text-gray-700 hover:text-gray-900'
                } ${item.color}`}
              >
                {item.label}
              </Link>
            ))}
          </nav>

          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-toggle-button"
            type="button"
            aria-label={isMobileMenuOpen ? 'Fermer le menu' : 'Ouvrir le menu'}
            aria-expanded={isMobileMenuOpen}
            aria-controls="mobile-navigation"
            className="lg:hidden p-2 shrink-0"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <nav id="mobile-navigation" data-testid="mobile-navigation" aria-label="Navigation mobile" className="lg:hidden mt-4 pb-4 space-y-3">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setIsMobileMenuOpen(false)}
                data-testid={`mobile-nav-${item.path.slice(1) || 'home'}-link`}
                aria-current={location.pathname === item.path ? 'page' : undefined}
                className={`block py-2 font-semibold transition-colors ${
                  location.pathname === item.path
                    ? item.color || 'text-gray-900'
                    : 'text-gray-700'
                } ${item.color}`}
              >
                {item.label}
              </Link>
            ))}
          </nav>
        )}
      </div>
    </header>
  );
};

export default Header;